import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import LandingPage from './components/Landing/LandingPage';
import LegalPage from './components/Legal/LegalPage';
import LoginPage from './components/Auth/LoginPage';
import HomePage from './components/Home/HomePage';
import TeacherDashboard from './components/Teacher/TeacherDashboard';
import TeacherSearchPage from './components/Teacher/TeacherSearchPage';
import ConversationPage from './components/Conversation/ConversationPage';
import FlashcardsPage from './components/Flashcards/FlashcardsPage';
import ProfilePage from './components/Profile/ProfilePage';
import ProgressPage from './components/Progress/ProgressPage';
import AdminDashboard from './components/Admin/AdminDashboard';
import VideoMeetingPage from './components/Meeting/VideoMeetingPage';
import MeetingRoom from './components/Meeting/MeetingRoom';
import GuestMeetingPage from './components/Meeting/GuestMeetingPage';
import GuestThankYouPage from './components/Meeting/GuestThankYouPage';
import MessagesPage from './components/Messages/MessagesPage';
import './App.css';

// Google OAuth Client ID from environment
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: '#757575'
      }}>
        Loading...
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/home" />;
};

// Public Route Component (redirect to home if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: '#757575'
      }}>
        Loading...
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/home" />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - OAuth Login Strategy */}
      {/*
        Authentication Strategy:
        - Web: Users sign up/sign in with Google OAuth (auto-detects new vs existing)
        - Mobile: Users sign up for 7-day trial with Google/Apple OAuth (required)
        - Guests: Can join meetings without account (limited access)
        - Reasoning: OAuth prevents trial abuse, enables App Store subscription tracking
      */}
      {/* Legacy login page - kept for backward compatibility with old bookmarks */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Home Route - handles its own authentication (shows LoginScreen if not authenticated) */}
      <Route
        path="/home"
        element={<HomePage />}
      />

      {/* Protected Routes */}
      <Route
        path="/teacher-dashboard"
        element={
          <ProtectedRoute>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/find-teachers"
        element={
          <ProtectedRoute>
            <TeacherSearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations"
        element={
          <ProtectedRoute>
            <ConversationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flashcards"
        element={
          <ProtectedRoute>
            <FlashcardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/progress"
        element={
          <ProtectedRoute>
            <ProgressPage />
          </ProtectedRoute>
        }
      />

      {/* Video Meeting Routes */}
      <Route
        path="/video-meeting"
        element={
          <ProtectedRoute>
            <VideoMeetingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meetings/:id"
        element={
          <ProtectedRoute>
            <MeetingRoom />
          </ProtectedRoute>
        }
      />
      {/* Guest Meeting Route - NO authentication required */}
      <Route
        path="/meet/:roomId"
        element={<GuestMeetingPage />}
      />
      {/* Guest Thank You Page - NO authentication required */}
      <Route
        path="/guest-thank-you"
        element={<GuestThankYouPage />}
      />

      {/* Admin Route (uses its own secret key auth) */}
      <Route path="/admin" element={<AdminDashboard />} />

      {/* Legal/Terms Page - Public */}
      <Route path="/legal" element={<LegalPage />} />

      {/* Landing Page - World-facing homepage */}
      <Route path="/" element={<LandingPage />} />
      {/* Catch-all - Redirect to landing page */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  // Only wrap with GoogleOAuthProvider if client ID is configured
  const content = (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );

  if (GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'your-google-client-id-here.apps.googleusercontent.com') {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {content}
      </GoogleOAuthProvider>
    );
  }

  // If Google OAuth not configured, still render app (Google linking will be disabled)
  console.warn('Google OAuth Client ID not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env file.');
  return content;
}

export default App;
